/**
 * AppShell: schmale Sidebar links (Marke + gruppierte Nav), View-Bereich rechts
 * (minimale Topbar mit Breadcrumb, Server-Switcher, Live-Indikator, Logout) und
 * der geroutete Inhalt. Ohne Session: Login. Admin-Views sind für Nicht-Admins
 * ausgeblendet (die Server-Guards bleiben die Wahrheit).
 */

import { useSignal, useSignalEffect } from "@preact/signals";
import { session, servers, activeServer, wsStatus, hasPermission, setActiveServer, logout } from "./store";
import { t, lang, setLanguage, LANGUAGES, type TranslationKey } from "./i18n";
import { route, navigate, type ViewId } from "./router";
import { visibleNavItems } from "./nav-permissions";
import { serverSwitcherState } from "./server-switcher";
import { buildInfo, formatBuildInfo, loadBuildInfo } from "./build-info";
import { toasts } from "./toast";
import { Login } from "./views/login";
import { Dashboard } from "./views/dashboard";
import { Players } from "./views/players";
import { PlayerIntel } from "./views/player-intel";
import { Console } from "./views/console";
import { Settings } from "./views/settings";
import { Announcer } from "./views/announcer";
import { JoinAnnouncer } from "./views/join-announcer";
import { Log } from "./views/log";
import { AuditLog } from "./views/audit-log";
import { Commands } from "./views/commands";
import { Chat } from "./views/chat";
import { Maps } from "./views/maps";
import { Bans } from "./views/bans";
import { Users } from "./views/users";
import { Servers } from "./views/servers";
import { IngameAdmins } from "./views/ingame-admins";
import { AgentLive } from "./views/agent-live";
import { Files } from "./views/files";

export interface NavItem {
  id: ViewId;
  labelKey: TranslationKey;
  icon: string;
  admin?: boolean;
  permission: string;
}

const NAV: NavItem[] = [
  { id: "dashboard", labelKey: "nav.dashboard", icon: "▦", permission: "view.dashboard" },
  { id: "players", labelKey: "nav.players", icon: "▣", permission: "view.players" },
  { id: "maps", labelKey: "nav.maps", icon: "◈", permission: "view.maps" },
  { id: "files", labelKey: "nav.files", icon: "⧉", permission: "view.files" },
  { id: "chat", labelKey: "nav.chat", icon: "✎", permission: "view.chat" },
  { id: "announcer", labelKey: "nav.announcer", icon: "✉", permission: "view.announcer" },
  { id: "join-announcer", labelKey: "nav.joinAnnouncer", icon: "⇄", permission: "view.join_announcer" },
  { id: "bans", labelKey: "nav.bans", icon: "⊘", permission: "view.bans" },
  { id: "commands", labelKey: "nav.commands", icon: "✦", permission: "view.commands" },
  { id: "settings", labelKey: "nav.settings", icon: "⚙", permission: "view.settings" },
  { id: "console", labelKey: "nav.console", icon: "›_", permission: "view.console" },
  { id: "player-intel", labelKey: "nav.playerIntel", icon: "◎", admin: true, permission: "view.player_intel" },
  { id: "agent-live", labelKey: "nav.agentLive", icon: "⚡", admin: true, permission: "view.agent_events" },
  { id: "log", labelKey: "nav.log", icon: "≣", admin: true, permission: "view.logs" },
  { id: "audit-log", labelKey: "nav.auditLog", icon: "⎙", admin: true, permission: "view.audit_log" },
  { id: "users", labelKey: "nav.users", icon: "⊕", admin: true, permission: "view.users" },
  { id: "ingame-admins", labelKey: "nav.ingameAdmins", icon: "⛨", admin: true, permission: "action.manage_ingame_admins" },
  { id: "servers", labelKey: "nav.servers", icon: "▤", admin: true, permission: "view.servers" },
];

/**
 * Themes: `data-theme`-Attribut am <html> + Persistenz in localStorage. Jedes
 * Theme ist ein reiner CSS-Token-Override-Block in `styles.css` (kein View-
 * Hardcode). `dark` (Default) lebt in `:root`, alle anderen als
 * `html[data-theme="<id>"] { … }`. Neue Themes hier + im CSS ergänzen.
 */
export const THEMES = [
  { id: "dark", labelKey: "theme.dark" },
  { id: "light", labelKey: "theme.light" },
  { id: "blood-forest", labelKey: "theme.bloodForest" },
  { id: "aurora", labelKey: "theme.aurora" },
  { id: "synth", labelKey: "theme.synth" },
] as const;
export type Theme = (typeof THEMES)[number]["id"];
const THEME_IDS = new Set<string>(THEMES.map((t) => t.id));
const THEME_KEY = "bf2142_theme";

function readTheme(): Theme {
  try {
    const v = localStorage.getItem(THEME_KEY) ?? "";
    return THEME_IDS.has(v) ? (v as Theme) : "dark";
  } catch {
    return "dark";
  }
}

function applyTheme(theme: Theme): void {
  if (typeof document === "undefined") return;
  document.documentElement.dataset.theme = theme;
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch {
    /* localStorage kann in restriktiven Browser-Kontexten scheitern. */
  }
}

function Toasts() {
  return (
    <div class="toasts">
      {toasts.value.map((t) => (
        <div key={t.id} class={`toast ${t.type}`}>{t.msg}</div>
      ))}
    </div>
  );
}

function Placeholder({ title }: { title: string }) {
  return (
    <div class="view-enter">
      <div class="page-head">
        <div><h1>{title}</h1><div class="sub">{t("shell.comingSoonTitle")}</div></div>
      </div>
      <div class="panel"><div class="panel-body empty">{t("shell.comingSoon")}</div></div>
    </div>
  );
}

function ViewContent() {
  switch (route.value) {
    case "dashboard": return <Dashboard />;
    case "players": return <Players />;
    case "player-intel": return <PlayerIntel />;
    case "console": return <Console />;
    case "settings": return <Settings />;
    case "announcer": return <Announcer />;
    case "join-announcer": return <JoinAnnouncer />;
    case "commands": return <Commands />;
    case "chat": return <Chat />;
    case "maps": return <Maps />;
    case "files": return <Files />;
    case "bans": return <Bans />;
    case "users": return <Users />;
    case "ingame-admins": return <IngameAdmins />;
    case "servers": return <Servers />;
    case "log": return <Log />;
    case "audit-log": return <AuditLog />;
    case "agent-live": return <AgentLive />;
    default: {
      const item = NAV.find((n) => n.id === route.value);
      return <Placeholder title={item ? t(item.labelKey) : t("nav.players")} />;
    }
  }
}

export function App() {
  const navOpen = useSignal(false);
  const theme = useSignal<Theme>(readTheme());
  useSignalEffect(() => applyTheme(theme.value));
  loadBuildInfo(); // idempotent; Anzeige in Sidebar-Footer + Login

  if (!session.value) return (<><Login /><Toasts /></>);

  const visibleNav = visibleNavItems(NAV, hasPermission);
  const main = visibleNav.filter((n) => !n.admin);
  const admin = visibleNav.filter((n) => n.admin);
  const go = (id: ViewId) => {
    navigate(id);
    navOpen.value = false;
  };
  const NavBtn = (n: NavItem) => (
    <button key={n.id} class={route.value === n.id ? "active" : ""} onClick={() => go(n.id)}>
      <span class="ico">{n.icon}</span>
      {t(n.labelKey)}
    </button>
  );

  return (
    <div class={`shell ${navOpen.value ? "nav-open" : ""}`}>
      <div class="scrim" onClick={() => (navOpen.value = false)} />

      <aside class="sidebar">
        <div class="brand">
          <div class="logo">
            {/* Lokales Logo; fehlt es, Bild ausblenden statt Broken-Image (kein Crash). */}
            <img src="/logo.png" alt="BF2142" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
          </div>
          <div>
            <div class="kicker">{t("shell.commandTerminal")}</div>
            <b>BF<span class="cy">2142</span></b>
            <div class="role">{t("shell.rconControl")}</div>
          </div>
        </div>
        <nav class="nav">
          {main.map(NavBtn)}
          {admin.length > 0 && (
            <>
              <div class="nav-label label">{t("nav.administration")}</div>
              {admin.map(NavBtn)}
            </>
          )}
        </nav>
        <div class="sidebar-footer">
          <div class="lang-switch" role="group" aria-label={t("shell.language")}>
            {LANGUAGES.map((l) => (
              <button
                key={l.id}
                class={`lang-btn ${lang.value === l.id ? "active" : ""}`}
                aria-pressed={lang.value === l.id}
                onClick={() => setLanguage(l.id)}
              >
                {l.label}
              </button>
            ))}
          </div>
          <label class="theme-toggle theme-select" title={t("shell.theme")}>
            <span class="ico">◐</span>
            <select
              value={theme.value}
              aria-label={t("shell.theme")}
              onChange={(e) => (theme.value = (e.target as HTMLSelectElement).value as Theme)}
            >
              {THEMES.map((th) => (
                <option key={th.id} value={th.id}>{t(th.labelKey)}</option>
              ))}
            </select>
          </label>
          {/* Sichtbare Release-Version (docs/RELEASE.md): welcher Build läuft live? */}
          <div class="build-info" title="Release / Build">{formatBuildInfo(buildInfo.value)}</div>
        </div>
      </aside>

      <div class="main">
        <header class="topbar">
          <button class="btn ghost sm menu-btn" onClick={() => (navOpen.value = true)} aria-label={t("shell.menu")}>☰</button>
          <span class="crumb">{(() => { const it = NAV.find((n) => n.id === route.value); return it ? t(it.labelKey) : t("nav.players"); })()}</span>
          <div class="grow" />
          {(() => {
            // Defensiv: ein aktiver Server ohne Live-Daten / nicht in der Liste
            // darf den Switcher-Render nie werfen (siehe server-switcher.ts).
            const sw = serverSwitcherState(servers.value, activeServer.value);
            return (
              <select
                class="select"
                style="width:auto;min-width:170px"
                value={sw.value}
                onChange={(e) => setActiveServer((e.target as HTMLSelectElement).value)}
              >
                {sw.empty && <option value="">{t("shell.noServer")}</option>}
                {sw.options.map((o) => (
                  <option key={o.id} value={o.id}>{o.missing ? `${o.label} (${t("shell.offlineSuffix")})` : o.label}</option>
                ))}
              </select>
            );
          })()}
          <span class="badge" title={`WebSocket: ${wsStatus.value}`}>
            <span class={`live-dot ${wsStatus.value}`} />
            {wsStatus.value === "open" ? t("shell.connected") : wsStatus.value === "connecting" ? t("shell.connecting") : t("shell.offline")}
          </span>
          <div class="usermenu">
            <span class="caption">{session.value.name} · {session.value.role}</span>
            <button class="btn sm ghost" onClick={() => void logout()}>{t("shell.logout")}</button>
          </div>
        </header>

        <main class="content">
          <ViewContent />
        </main>
      </div>
      <Toasts />
    </div>
  );
}
